import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  MessageSquare, Clock, CheckCircle, AlertCircle,
  User, Send
} from 'lucide-react';
import { toast } from 'sonner';

// Normalize backend URL to handle various formats
const normalizeBackendBase = (rawValue) => {
  const fallback = 'http://localhost:8000';
  const raw = String(rawValue || '').trim();
  if (!raw) return fallback;
  const noTrailingSlash = raw.replace(/\/+$/g, '');
  return noTrailingSlash.replace(/\/api$/i, '');
};

const BACKEND_BASE = normalizeBackendBase(process.env.REACT_APP_BACKEND_URL);
const API = `${BACKEND_BASE}/api`;

const AdminSupportPage = () => {
  const { token, admin } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [response, setResponse] = useState('');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchTickets = async () => {
    try {
      const params = filter ? `?status=${filter}` : '';
      const res = await fetch(`${API}/admin/support/tickets${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setTickets(await res.json());
      }
    } catch (error) {
      console.error('Failed to fetch tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTicket = async (ticketId, data) => {
    try {
      const res = await fetch(`${API}/admin/support/tickets/${ticketId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (res.ok) {
        const updated = await res.json();
        setSelectedTicket(updated);
        fetchTickets();
        if (data.response) {
          setResponse('');
          toast.success('Response sent');
        } else {
          toast.success('Ticket updated');
        }
      }
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const getStatusBadge = (status) => {
    const statuses = {
      open: { icon: AlertCircle, class: isDarkMode ? 'bg-amber-500/20 text-amber-400' : 'bg-amber-100 text-amber-700', label: 'Open' },
      in_progress: { icon: Clock, class: isDarkMode ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-700', label: 'In Progress' },
      resolved: { icon: CheckCircle, class: isDarkMode ? 'bg-emerald-500/20 text-emerald-400' : 'bg-emerald-100 text-emerald-700', label: 'Resolved' },
      closed: { icon: CheckCircle, class: isDarkMode ? 'bg-slate-500/20 text-slate-400' : 'bg-gray-100 text-gray-600', label: 'Closed' }
    };
    const badge = statuses[status] || statuses.open;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${badge.class}`}>
        <Icon className="h-3 w-3" />
        {badge.label}
      </span>
    );
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Support Tickets</h1>
            <p className={`${theme.textMuted} mt-1`}>Manage customer support requests</p>
          </div>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className={`h-10 px-3 rounded-md border ${isDarkMode ? 'bg-slate-800/50 border-slate-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
          >
            <option value="">All Tickets</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Tickets List */}
          <div className="lg:col-span-1">
            <Card className={theme.card}>
              <CardHeader className="pb-2">
                <CardTitle className={`text-sm ${theme.text}`}>Tickets ({tickets.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                {loading ? (
                  <div className="text-center py-8">
                    <div className={`animate-spin rounded-full h-6 w-6 border-b-2 mx-auto ${isDarkMode ? 'border-emerald-500' : 'border-emerald-600'}`}></div>
                  </div>
                ) : tickets.length === 0 ? (
                  <p className={`text-center py-8 text-sm ${theme.textMuted}`}>No tickets found</p>
                ) : (
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {tickets.map((ticket) => (
                      <div
                        key={ticket.ticket_id}
                        onClick={() => setSelectedTicket(ticket)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedTicket?.ticket_id === ticket.ticket_id
                            ? isDarkMode ? 'bg-emerald-500/20 border border-emerald-500/50' : 'bg-emerald-50 border border-emerald-300'
                            : isDarkMode ? 'bg-slate-700/30 hover:bg-slate-700/50' : 'bg-gray-50 hover:bg-gray-100'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <p className={`font-medium text-sm truncate flex-1 ${theme.text}`}>
                            {ticket.subject}
                          </p>
                          {getStatusBadge(ticket.status)}
                        </div>
                        <p className={`text-xs mb-2 line-clamp-2 ${theme.textMuted}`}>{ticket.message}</p>
                        <div className={`flex justify-between items-center text-xs ${theme.textSubtle}`}>
                          <span>{ticket.user?.name || 'Unknown'}</span>
                          <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Ticket Detail */}
          <div className="lg:col-span-2">
            {selectedTicket ? (
              <Card className={theme.card}>
                <CardHeader className={`border-b ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className={theme.text}>{selectedTicket.subject}</CardTitle>
                      <div className={`flex items-center gap-3 mt-2 text-sm ${theme.textMuted}`}>
                        <span className="flex items-center gap-1">
                          <User className="h-4 w-4" />
                          {selectedTicket.user?.name} ({selectedTicket.user?.email})
                        </span>
                        <span>•</span>
                        <span>{selectedTicket.category}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(selectedTicket.status)}
                      <select
                        value={selectedTicket.status}
                        onChange={(e) => updateTicket(selectedTicket.ticket_id, { status: e.target.value })}
                        className={`h-8 px-2 text-xs rounded border ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  {/* Original Message */}
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-slate-700/30' : 'bg-gray-50'}`}>
                    <p className={`text-xs mb-2 ${theme.textSubtle}`}>
                      {new Date(selectedTicket.created_at).toLocaleString()}
                    </p>
                    <p className={theme.textMuted}>{selectedTicket.message}</p>
                  </div>

                  {/* Responses */}
                  {selectedTicket.responses?.length > 0 && (
                    <div className="space-y-3">
                      {selectedTicket.responses.map((resp, idx) => (
                        <div key={idx} className={`p-4 rounded-lg ${isDarkMode ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-emerald-50 border border-emerald-200'}`}>
                          <div className="flex justify-between items-center mb-2">
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                              {resp.admin_name} (Admin)
                            </span>
                            <span className={`text-xs ${theme.textSubtle}`}>
                              {new Date(resp.created_at).toLocaleString()}
                            </span>
                          </div>
                          <p className={theme.textMuted}>{resp.message}</p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reply Form */}
                  {selectedTicket.status !== 'closed' && (
                    <div className={`pt-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                      <Label className={`${theme.text} mb-2 block`}>Reply</Label>
                      <textarea
                        value={response}
                        onChange={(e) => setResponse(e.target.value)}
                        className={`w-full h-24 px-3 py-2 rounded-md border resize-none ${isDarkMode ? 'bg-slate-900/50 border-slate-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        placeholder="Type your response..."
                      />
                      <div className="flex justify-end mt-2">
                        <Button
                          onClick={() => updateTicket(selectedTicket.ticket_id, { response })}
                          disabled={!response.trim()}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Send className="h-4 w-4 mr-2" /> Send Reply
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className={`h-full ${theme.card}`}>
                <CardContent className="flex items-center justify-center h-full py-12">
                  <div className="text-center">
                    <MessageSquare className={`h-12 w-12 mx-auto mb-4 ${theme.textMuted}`} />
                    <p className={theme.textMuted}>Select a ticket to view details</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSupportPage;
